import React, { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LOGO, CHART } from "@/lib/helpers";
import { Printer, FileSpreadsheet, RefreshCw, FileText } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Legend } from "recharts";

const downloadBlob = async (path, filename) => {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

export default function Reports() {
  const printRef = useRef();
  const { data: perf, isLoading, refetch, isRefetching } = useQuery({ queryKey: ["performance"], queryFn: async () => (await api.get("/performance")).data });
  const { data: exec } = useQuery({ queryKey: ["executive"], queryFn: async () => (await api.get("/dashboard/executive")).data });

  const ranking = perf?.ranking || [];
  const trend = exec?.charts?.trend || [];

  const exportExcel = async () => {
    try { await downloadBlob("/reports/excel", "VNS_Employee_Ranking.xlsx"); toast.success("Excel workbook downloaded"); }
    catch { toast.error("Excel export failed"); }
  };

  const exportPdf = async () => {
    try { await downloadBlob("/reports/pdf", "VNS_Performance_Report.pdf"); toast.success("PDF report downloaded"); }
    catch { toast.error("PDF export failed"); }
  };

  const handlePrint = () => window.print();

  if (isLoading) return (<div><PageHeader title="Reports & Export" /><Skeleton className="h-64 rounded-lg" /></div>);

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports & Export" subtitle="Weekly / monthly reports, trends & downloadable exports">
        <Button onClick={() => refetch()} variant="outline" data-testid="report-refresh-btn" className="rounded-lg"><RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} /> Refresh</Button>
        <Button onClick={exportExcel} data-testid="export-excel-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"><FileSpreadsheet className="mr-2 h-4 w-4" /> Export Excel</Button>
        <Button onClick={exportPdf} data-testid="export-pdf-btn" className="bg-gold-dark hover:bg-gold text-white rounded-lg"><FileText className="mr-2 h-4 w-4" /> Export PDF</Button>
        <Button onClick={handlePrint} data-testid="print-report-btn" className="bg-navy hover:bg-navy-900 text-white rounded-lg"><Printer className="mr-2 h-4 w-4" /> Print A4</Button>
      </PageHeader>

      <Tabs defaultValue="ranking" className="no-print">
        <TabsList className="mb-4">
          <TabsTrigger value="ranking" data-testid="tab-ranking">Employee Ranking</TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">Weekly Trends</TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">Productivity</TabsTrigger>
        </TabsList>

        <TabsContent value="ranking">
          <SectionCard title="Employee Ranking Report">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader><TableRow>{["Rank", "Employee", "Completion %", "Accuracy %", "Attendance %", "Overall %"].map((h) => <TableHead key={h} className="text-navy font-semibold">{h}</TableHead>)}</TableRow></TableHeader>
                <TableBody>
                  {ranking.map((r) => (
                    <TableRow key={r.employee_id}><TableCell className="font-bold text-navy">{r.rank}</TableCell><TableCell>{r.name}</TableCell><TableCell>{r.completion}%</TableCell><TableCell>{r.accuracy}%</TableCell><TableCell>{r.attendance}%</TableCell><TableCell className="font-semibold">{r.overall}%</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="weekly">
          <SectionCard title="Weekly Attendance & Accuracy Trend">
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: "#64748b" }} /><YAxis tick={{ fontSize: 12, fill: "#64748b" }} /><Tooltip /><Legend />
                <Line type="monotone" dataKey="present" name="Present" stroke={CHART.navy} strokeWidth={2} />
                <Line type="monotone" dataKey="accuracy" name="Accuracy %" stroke={CHART.gold} strokeWidth={2} />
                <Line type="monotone" dataKey="hours" name="Hours" stroke={CHART.green} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </SectionCard>
        </TabsContent>

        <TabsContent value="monthly">
          <SectionCard title="Productivity by Employee">
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={ranking}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748b" }} angle={-15} textAnchor="end" height={60} /><YAxis tick={{ fontSize: 12, fill: "#64748b" }} /><Tooltip /><Legend />
                <Bar dataKey="productivity" name="Productivity %" fill={CHART.navy} radius={[4, 4, 0, 0]} />
                <Bar dataKey="completion" name="Completion %" fill={CHART.gold} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </SectionCard>
        </TabsContent>
      </Tabs>

      {/* Printable A4 report */}
      <div ref={printRef} className="print-area">
        <div style={{ display: "flex", alignItems: "center", gap: 16, borderBottom: "3px solid #0B2545", paddingBottom: 16, marginBottom: 24 }}>
          <img src={LOGO} alt="VNS" style={{ height: 64, width: 64, objectFit: "contain" }} />
          <div>
            <div style={{ fontFamily: "Chivo, sans-serif", fontSize: 22, fontWeight: 800, color: "#0B2545" }}>VNS Vinayaka Group of Enterprises</div>
            <div style={{ color: "#64748b", fontSize: 13 }}>Data Entry Division — Performance & Ranking Report</div>
            <div style={{ color: "#C5A059", fontSize: 12 }}>Generated on {new Date().toLocaleString("en-GB")}</div>
          </div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#0B2545", color: "#fff" }}>
              {["Rank", "Employee", "Assigned", "Completed", "Completion %", "Accuracy %", "Attendance %", "Overall %"].map((h) => <th key={h} style={{ padding: 8, textAlign: "left" }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {ranking.map((r, i) => (
              <tr key={r.employee_id} style={{ background: i % 2 ? "#f8fafc" : "#fff", borderBottom: "1px solid #e2e8f0" }}>
                <td style={{ padding: 8 }}>{r.rank}</td><td style={{ padding: 8 }}>{r.name}</td><td style={{ padding: 8 }}>{r.assigned}</td><td style={{ padding: 8 }}>{r.completed}</td><td style={{ padding: 8 }}>{r.completion}%</td><td style={{ padding: 8 }}>{r.accuracy}%</td><td style={{ padding: 8 }}>{r.attendance}%</td><td style={{ padding: 8, fontWeight: 700, color: "#0B2545" }}>{r.overall}%</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ marginTop: 40, textAlign: "center", color: "#64748b", fontSize: 11, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
          Generated by VNS Vinayaka Group of Enterprises
        </div>
      </div>
    </div>
  );
}
